'use client';

import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React, {
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AbgTypography,
  ArrowDownIcon,
  CheckIcon,
  mergeStyleParts,
} from '@abg/kit-base';
import { AbgSxProps } from '@abg/typings';

import { useAnalyticsGeneralEngagement } from '../../shared/analytics/hooks/use-analytics-general-engagement';

import { dropdownBaseStyleParts } from './Dropdown.styling';
import { DropdownProperties, DropdownStyleParts, MenuItemModel } from './types';

// Custom Arrow Icon with Specified stylings
const CustomArrow = ({
  open,
  styles,
}: {
  open: boolean;
  styles: AbgSxProps;
}) => (
  <ArrowDownIcon
    sx={{
      ...styles,
      transition: 'transform 0.3s ease-in-out', // Smooth animation
      transform: open ? 'rotate(180deg)' : 'rotate(0deg)', // Rotate when open
    }}
  />
);

const RenderIconComponent = ({
  onIconClick,
  styleParts = {},
  open,
}: {
  onIconClick: (event: MouseEvent<HTMLDivElement>) => void;
  styleParts?: DropdownStyleParts;
  open: boolean;
}) => (
  <Box
    onClick={onIconClick}
    sx={mergeStyleParts(
      dropdownBaseStyleParts,
      styleParts,
      'selectMenuIconWrapper',
    )}
    data-testid="icon-component-wrapper"
  >
    <CustomArrow
      open={open}
      styles={mergeStyleParts(
        dropdownBaseStyleParts,
        styleParts,
        'selectMenuArrowIcon',
      )}
      data-testid="icon-component-wrapper-arrow"
    />
  </Box>
);
export const Dropdown = ({
  defaultValue,
  options = [],
  onChangeHandler,
  onItemClick,
  styleParts = {},
  placeholder,
  activeValue,
  ...other
}: DropdownProperties) => {
  const [open, setOpen] = useState<boolean>(false);
  const inputReference = useRef<HTMLInputElement | null>(null);

  const initialValue =
    activeValue || defaultValue || placeholder || options[0]?.value || '';

  const [selected, setSelected] = useState(initialValue);

  const { emitEvent: emitGeneralEngagementEvent } =
    useAnalyticsGeneralEngagement();

  const optionMap: Map<string, MenuItemModel> = useMemo(() => {
    const map = new Map<string, MenuItemModel>();
    for (const item of options) {
      map.set(item.value, item);
    }
    return map;
  }, [options]);

  useEffect(() => {
    setSelected(initialValue);
  }, [initialValue]);

  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    !activeValue && setSelected(value);
    onChangeHandler(value, optionMap.get(value) as MenuItemModel);
  };

  const onIconClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
    const comboBox = inputReference.current?.querySelector(
      '[role="combobox"]',
    ) as HTMLInputElement;
    if (comboBox) {
      comboBox.focus();
    }
  }, []);

  const IconComponent = useCallback(
    (props: Record<string, unknown>) => (
      <RenderIconComponent
        onIconClick={onIconClick}
        styleParts={styleParts}
        open={open}
        {...props}
      />
    ),
    [onIconClick, styleParts, open],
  );

  return (
    <Select
      value={selected}
      onChange={handleChange}
      open={open}
      sx={mergeStyleParts(dropdownBaseStyleParts, styleParts, 'selectMenu')}
      onOpen={(event) => {
        emitGeneralEngagementEvent(event.currentTarget, 'Dropdown: Open');
        setOpen(true);
      }}
      onClose={(event) => {
        emitGeneralEngagementEvent(event.currentTarget, 'Dropdown: Close');
        setOpen(false);
      }}
      data-testid="dropdown-select-component"
      inputProps={{
        'aria-label': 'Select an option',
      }}
      renderValue={(value) => {
        return (
          optionMap.get(value)?.label ||
          (placeholder ? (
            <Box
              sx={mergeStyleParts(
                dropdownBaseStyleParts,
                styleParts,
                'placeholder',
              )}
              aria-haspopup="listbox"
              data-testid="listbox-placeholder"
            >
              {placeholder}
            </Box>
          ) : (
            ''
          ))
        );
      }}
      ref={inputReference}
      IconComponent={IconComponent}
      MenuProps={{
        sx: mergeStyleParts(
          dropdownBaseStyleParts,
          styleParts,
          'selectMenuPopover',
        ),
        PaperProps: {
          sx: mergeStyleParts(
            dropdownBaseStyleParts,
            styleParts,
            'selectMenuList',
          ),
        },
      }}
      {...other}
    >
      {options.map((item: MenuItemModel) => (
        <MenuItem
          key={item.value}
          value={item.value}
          data-testid={`dropdown-select-dropdown-item-${item.value}`}
          sx={mergeStyleParts(
            dropdownBaseStyleParts,
            styleParts,
            'selectMenuItem',
          )}
          onClick={() => {
            if (onItemClick) {
              onItemClick(item.value, item);
            }
          }}
        >
          <AbgTypography
            variant="body2"
            data-testid={`dropdown-select-dropdown-item-text-${item.value}`}
          >
            {item.label}
          </AbgTypography>
          <CheckIcon
            sx={mergeStyleParts(
              dropdownBaseStyleParts,
              styleParts,
              'selectMenuItemIcon',
            )}
          />
        </MenuItem>
      ))}
    </Select>
  );
};
